import React from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchToken, logout } from "../../../api_calls/requestConfig";
import {
  COOKIE_MAX_AGE_MS,
  evaluateSessionExpiry,
  getSessionEndsAtMs,
  SESSION_CHECK_INTERVAL_MS,
  setSessionEndsAt,
} from "../../../api_calls/authTokenUtils";
import { BROWSER_COMPAT_TOAST_OPTIONS } from "../Hoc/accessControlToasts";
const PUBLIC_PATHS = ["/", "/login", "/forgot-password"];
const SESSION_EXPIRY_TOAST_ID = "session-expiry-warning";
const SESSION_EXPIRY_WARNING_DISMISSED_KEY = "sessionExpiryWarningDismissedFor";
const resolveSessionEndsAtMs = (hasToken) => {
  let sessionEndsAtMs = getSessionEndsAtMs();
  if (sessionEndsAtMs === null && hasToken) {
    setSessionEndsAt(Date.now() + COOKIE_MAX_AGE_MS);
    sessionEndsAtMs = getSessionEndsAtMs();
  }
  return sessionEndsAtMs;
};

const isWarningDismissed = (sessionEndsAtMs) =>
  typeof sessionEndsAtMs === "number" &&
  sessionStorage.getItem(SESSION_EXPIRY_WARNING_DISMISSED_KEY) === String(sessionEndsAtMs);
const dismissWarning = (sessionEndsAtMs) => {
  if (typeof sessionEndsAtMs !== "number") {
    return;
  }
  sessionStorage.setItem(SESSION_EXPIRY_WARNING_DISMISSED_KEY, String(sessionEndsAtMs));
};

const getSessionExpiryToastOptions = (sessionEndsAtMs) => ({
  ...BROWSER_COMPAT_TOAST_OPTIONS,
  toastId: SESSION_EXPIRY_TOAST_ID,
  closeButton: true,
  closeOnClick: false,
  draggable: false,
  onClose: () => {
    dismissWarning(sessionEndsAtMs);
  },
});

const showWarningToast = (minutesRemaining, sessionEndsAtMs) => {
  if (isWarningDismissed(sessionEndsAtMs)) {
    return;
  }
  const minutes = typeof minutesRemaining === "number" ? minutesRemaining : 1;
  const minuteLabel = minutes === 1 ? "minute" : "minutes";
  const message = `Your session is expiring. You will be logged out in about ${minutes} ${minuteLabel}.`;
  const options = getSessionExpiryToastOptions(sessionEndsAtMs);
  if (toast.isActive(SESSION_EXPIRY_TOAST_ID) === true) {
    toast.update(SESSION_EXPIRY_TOAST_ID, { render: message, ...options });
    return;
  }
  toast(message, options);
};

const hideWarningToast = () => {
  toast.dismiss(SESSION_EXPIRY_TOAST_ID);
};

/**
 * App-wide session watchdog (mounted next to AnnouncementInboxHost inside BrowserRouter).
 * Session warning uses react-toastify (same style as browser compat); close dismisses until logout.
 */
const SessionExpiryHost = () => {
  const location = useLocation();
  const logoutTriggeredRef = React.useRef(false);

  const runSessionCheck = React.useCallback(() => {
    if (PUBLIC_PATHS.includes(location.pathname)) {
      hideWarningToast();
      return;
    }

    const token = fetchToken();
    if (token === "") {
      hideWarningToast();
      return;
    }

    const sessionEndsAtMs = resolveSessionEndsAtMs(true);
    const evaluation = evaluateSessionExpiry(Date.now(), sessionEndsAtMs);
    if (evaluation.shouldRun !== true) {
      hideWarningToast();
      return;
    }

    if (evaluation.shouldLogout === true) {
      hideWarningToast();
      if (logoutTriggeredRef.current !== true) {
        logoutTriggeredRef.current = true;
        logout();
      }
      return;
    }

    if (evaluation.showBanner === true) {
      showWarningToast(evaluation.minutesRemaining, sessionEndsAtMs);
      return;
    }
    hideWarningToast();
  }, [location.pathname]);

  React.useEffect(() => {
    logoutTriggeredRef.current = false;
    runSessionCheck();

    const intervalId = window.setInterval(runSessionCheck, SESSION_CHECK_INTERVAL_MS);
    
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        runSessionCheck();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
   
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      hideWarningToast();
    };
  }, [runSessionCheck]);
  return null;
};

export default SessionExpiryHost;